import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { authApi } from '../lib/api'
import { useMarkdownHtml } from '../utils/useMarkdownHtml'
import { isAdminPath } from '../utils/adminArea'
import terms from '../../../legal/terms-of-use.md?raw'
import privacy from '../../../legal/privacy-policy.md?raw'
import acceptableUse from '../../../legal/acceptable-use-policy.md?raw'

// The admin console is staff-only and never shows this. Pages that are open without accepting: the documents themselves, and the sign-up steps that ask for acceptance.
const OPEN_PATHS = ['/terms', '/privacy', '/acceptable-use', '/complete-profile', '/complete-registration', '/auth/callback']

const DOCUMENTS = [
  { id: 'terms', label: 'Terms of Use', markdown: terms },
  { id: 'privacy', label: 'Privacy Policy', markdown: privacy },
  { id: 'aup', label: 'Acceptable Use Policy', markdown: acceptableUse },
]

// Whoever has not accepted the current version of our legal documents sees this and nothing else: they
// accept (it is recorded against that version) or reject (it is recorded and they are signed out).
export default function TermsGate() {
  const { user, isLoading, refreshUser, logout } = useAuth()
  const { pathname } = useLocation()
  const [tab, setTab] = useState(DOCUMENTS[0].id)
  const [busy, setBusy] = useState<'accept' | 'reject' | null>(null)
  const [error, setError] = useState('')
  const [outdated, setOutdated] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)
  const doc = DOCUMENTS.find((d) => d.id === tab) ?? DOCUMENTS[0]
  const html = useMarkdownHtml(doc.markdown)

  const version = user?.currentTermsVersion ?? ''
  const open = !isLoading && !!user && user.termsAccepted === false && !OPEN_PATHS.includes(pathname) && !isAdminPath(pathname)

  // While it is up, keep the page behind it still and keep keyboard focus inside it.
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    dialogRef.current?.focus()
    return () => { document.body.style.overflow = previous }
  }, [open])

  if (!open) return null

  const trapFocus = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !dialogRef.current) return
    const items = dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled])')
    if (items.length === 0) return
    const first = items[0], last = items[items.length - 1]
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
  }

  const accept = async () => {
    setBusy('accept')
    setError('')
    try {
      await authApi.acceptTerms(version)
      await refreshUser()
    } catch (err: any) {
      if (err?.response?.data?.code === 'TERMS_VERSION_CHANGED') setOutdated(true)
      else setError(err?.response?.data?.error || 'We could not record your answer. Please try again.')
    } finally {
      setBusy(null)
    }
  }

  const reject = async () => {
    setBusy('reject')
    try { await authApi.declineTerms(version) } catch { /* sign out regardless */ }
    logout()
  }

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center p-3 sm:p-6" style={{ background: 'rgba(0,0,0,0.6)' }}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="terms-gate-title"
        tabIndex={-1}
        onKeyDown={trapFocus}
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col outline-none"
      >
        <div className="px-5 sm:px-8 pt-5 sm:pt-7 pb-3 border-b border-[var(--border)]">
          <h2 id="terms-gate-title" className="text-xl sm:text-2xl font-bold text-[var(--text-primary)]">Please review and accept our updated terms</h2>
          <p className="text-sm text-[var(--text-secondary)] mt-1">To keep using igotthatus.com, you need to accept the current Terms of Use, Privacy Policy and Acceptable Use Policy (version {version}). Until you do, you can look around but not rent, list or message.</p>
          <div role="tablist" aria-label="Documents" className="flex flex-wrap gap-1 mt-3">
            {DOCUMENTS.map((d) => (
              <button key={d.id} role="tab" aria-selected={tab === d.id} type="button" onClick={() => setTab(d.id)} className={`px-3 min-h-[44px] rounded-lg text-sm font-medium ${tab === d.id ? 'bg-[var(--forest-green)] text-white' : 'bg-[var(--bg-tint)] text-[var(--text-secondary)]'}`}>{d.label}</button>
            ))}
          </div>
        </div>

        <div className="px-5 sm:px-8 py-4 overflow-y-auto min-h-0 flex-1" tabIndex={0} aria-label={doc.label}>
          {html ? <div className="markdown-body" dangerouslySetInnerHTML={{ __html: html }} /> : <p className="text-[var(--text-secondary)]">Loading...</p>}
        </div>

        <div className="px-5 sm:px-8 py-4 border-t border-[var(--border)] bg-[var(--bg-primary)] rounded-b-2xl">
          {outdated && <div role="alert" className="bg-[#FEF3C7] border border-[#FCD34D] text-[#92400E] rounded-lg p-3 mb-3 text-sm">The terms were just updated again. Please reload the page to see the newest version.</div>}
          {error && <div role="alert" className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-3 text-sm">{error}</div>}
          <p className="text-xs text-[var(--text-muted)] mb-3">By accepting you agree to all three documents, including that disputes with igotthatus.com are settled by individual arbitration and not in court or as a class action (with a 30-day right to opt out, explained in the Terms). If you reject, you will be signed out.</p>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button type="button" onClick={reject} disabled={busy !== null} className="px-5 min-h-[44px] border border-[var(--border)] rounded-lg bg-white font-medium text-[var(--text-secondary)] disabled:opacity-50">{busy === 'reject' ? 'Signing out…' : 'Reject and sign out'}</button>
            {outdated
              ? <button type="button" onClick={() => window.location.reload()} className="px-5 min-h-[44px] rounded-lg bg-[var(--forest-green)] text-white font-semibold">Reload</button>
              : <button type="button" onClick={accept} disabled={busy !== null || !version} className="px-5 min-h-[44px] rounded-lg bg-[var(--forest-green)] text-white font-semibold disabled:opacity-50">{busy === 'accept' ? 'Saving…' : 'I accept'}</button>}
          </div>
        </div>
      </div>
    </div>
  )
}
